/**
 * CSK Seam and Bridge contracts — first-class, reusable interfaces for the seam
 * boundaries and admitted bridges that demos and realizations previously modeled
 * locally.
 *
 * A seam is a composition boundary where safety must be re-verified; a bridge is
 * an admitted arrow across a seam. Every theorem-governed field on a seam
 * (theorem, generator, neutralization mode) is derived from
 * `NeutralizationSecurityGuarantee` through {@link defineSeam}, so a
 * theorem/generator/mode mismatch is impossible to construct and fails schema
 * validation at DCS boundaries.
 *
 * Agda source: Foundation/Seam/, Foundation/Bridge/
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import { z } from "zod";

import type {
  Brand,
  GeneratorCode,
  NeutralizationModeName,
  NeutralizationTheoremCode,
  SeamType,
} from "./types.js";
import { NEUTRALIZATION_THEOREM_CODES, SEAM_TYPES } from "./types.js";
import type { NeutralizationSecurityGuarantee } from "../shared/canonical.js";
import { getNeutralizationTheoremMeta } from "../shared/canonical.js";
import type {
  AdmissionRequest,
  BridgeReceipt,
  BridgeWitness,
  SeamNormalFormPhase,
  WitnessConsumption,
  WitnessKind,
  WitnessScope,
} from "./bridge-witnesses.js";
import { WITNESS_KINDS, admitBridgeWitness, witnessKindForTheorem } from "./bridge-witnesses.js";

// ── Identifiers ────────────────────────────────────────────────

/** A seam identifier — branded to prevent accidental substitution. */
export type SeamId = Brand<string, "SeamId">;

/** A bridge identifier — branded to prevent accidental substitution. */
export type BridgeId = Brand<string, "BridgeId">;

/** Construct a seam identifier. */
export function seamId(value: string): SeamId {
  return value as SeamId;
}

/** Construct a bridge identifier. */
export function bridgeId(value: string): BridgeId {
  return value as BridgeId;
}

// ── Seam orientation ───────────────────────────────────────────

/**
 * Seam orientation — an alias of the core {@link SeamType} so apps share one
 * vocabulary (`vertical`, `horizontal`, `diagonal`, `mixed`). Kept as a named
 * extension point for future orientation refinements.
 */
export type SeamOrientation = SeamType;

/** The seam orientations, re-exported for catalog validation. */
export const SEAM_ORIENTATIONS = SEAM_TYPES;

// ── Endpoints and governance artifacts ─────────────────────────

/** The kinds of governance artifact a bridge endpoint can name. */
export const GOVERNANCE_ARTIFACT_KINDS = [
  "allow-list",
  "bridge-registry",
  "witness",
  "receipt",
  "matrix",
  "policy",
] as const;

/** A governance-artifact kind. */
export type GovernanceArtifactKind = (typeof GOVERNANCE_ARTIFACT_KINDS)[number];

/**
 * A typed source or target of a bridge — an object, lane, agent, domain, or
 * governance artifact. Presentation-specific placement (layout coordinates,
 * route ids) stays in the app; only the semantic identity lives here.
 */
export interface BridgeEndpoint {
  readonly id: string;
  readonly role: "source" | "target";
  readonly lane: string | null;
  readonly agent: string | null;
  readonly domain: string | null;
  readonly artifact: GovernanceArtifactKind | null;
}

/** Build a {@link BridgeEndpoint}, defaulting the optional facets to `null`. */
export function bridgeEndpoint(
  role: "source" | "target",
  id: string,
  facets: {
    readonly lane?: string;
    readonly agent?: string;
    readonly domain?: string;
    readonly artifact?: GovernanceArtifactKind;
  } = {},
): BridgeEndpoint {
  return {
    id,
    role,
    lane: facets.lane ?? null,
    agent: facets.agent ?? null,
    domain: facets.domain ?? null,
    artifact: facets.artifact ?? null,
  };
}

// ── Seams ──────────────────────────────────────────────────────

/**
 * A non-empty witness-kind list. The first element is always the kind governed
 * by the seam's theorem; app-specific additional evidence may follow it.
 */
export type RequiredWitnessKinds = readonly [WitnessKind, ...WitnessKind[]];

function requiredWitnessKindsFor(
  theorem: NeutralizationTheoremCode,
  explicit: readonly WitnessKind[] | undefined,
): RequiredWitnessKinds {
  const governed = witnessKindForTheorem(theorem);
  const extras = explicit?.filter((kind) => kind !== governed) ?? [];
  return [governed, ...extras];
}

/**
 * A seam: a composition boundary governed by exactly one Neutralization
 * Theorem. Theorem, generator, and mode are projected from the guarantee, never
 * supplied independently, so they cannot drift apart.
 */
export interface CskSeam {
  readonly id: SeamId;
  readonly orientation: SeamOrientation;
  readonly theorem: NeutralizationSecurityGuarantee;
  readonly generator: GeneratorCode;
  readonly mode: NeutralizationModeName;
  readonly forbiddenPattern: string;
  readonly requiredWitnessKinds: RequiredWitnessKinds;
}

/** Fields a caller supplies to define a seam; theorem metadata is derived. */
export interface SeamDefinition {
  readonly id: string;
  readonly orientation: SeamOrientation;
  readonly theorem: NeutralizationTheoremCode;
  readonly forbiddenPattern: string;
  /** Defaults to the single witness kind the theorem governs. */
  readonly requiredWitnessKinds?: readonly WitnessKind[];
}

/**
 * Define a seam from a theorem code. The guarantee, generator, and mode are
 * derived from theorem metadata, making a theorem/generator/mode mismatch
 * structurally inexpressible through this builder.
 */
export function defineSeam(definition: SeamDefinition): CskSeam {
  const theorem = getNeutralizationTheoremMeta(definition.theorem);
  return {
    id: seamId(definition.id),
    orientation: definition.orientation,
    theorem,
    generator: theorem.generator,
    mode: theorem.mode,
    forbiddenPattern: definition.forbiddenPattern,
    requiredWitnessKinds: requiredWitnessKindsFor(theorem.code, definition.requiredWitnessKinds),
  };
}

// ── Bridges ────────────────────────────────────────────────────

/** The lifecycle state of a bridge instance. */
export type BridgeLifecycle = "proposed" | "admitted" | "rejected" | "consumed" | "expired";

/**
 * A bridge: an admitted (or proposed) arrow across a seam, carrying its
 * endpoints, the witness that admits it, its SNF phase coverage, lifecycle
 * state, consumption ledger, and any emitted receipt.
 */
export interface CskBridge {
  readonly id: BridgeId;
  readonly from: BridgeEndpoint;
  readonly to: BridgeEndpoint;
  readonly seam: CskSeam;
  readonly witness: BridgeWitness | null;
  readonly phaseCoverage: readonly SeamNormalFormPhase[];
  readonly lifecycle: BridgeLifecycle;
  readonly consumption: WitnessConsumption | null;
  readonly receipt: BridgeReceipt | null;
}

// ── Admission verdicts ─────────────────────────────────────────

/**
 * Why a bridge was admitted or rejected. Rejection distinguishes a missing
 * witness, a forbidden square, and an invalid lifecycle/resource/state, so the
 * UI can explain each case without re-deriving the reason.
 */
export type BridgeAdmissionVerdict =
  | { readonly status: "admitted"; readonly bridge: BridgeId; readonly receipt: BridgeReceipt }
  | {
      readonly status: "rejected-missing-witness";
      readonly bridge: BridgeId;
      readonly required: WitnessKind;
    }
  | {
      readonly status: "rejected-forbidden-square";
      readonly bridge: BridgeId;
      readonly reason: string;
    }
  | {
      readonly status: "rejected-invalid-lifecycle";
      readonly bridge: BridgeId;
      readonly reason: string;
    };

/** True when a bridge admission verdict admitted the bridge. */
export function isBridgeAdmitted(
  verdict: BridgeAdmissionVerdict,
): verdict is Extract<BridgeAdmissionVerdict, { status: "admitted" }> {
  return verdict.status === "admitted";
}

/** Inputs to {@link admitBridge} beyond the bridge itself. */
export interface BridgeAdmissionContext {
  /** The scope the admission is requested within. */
  readonly scope: WitnessScope;
  /**
   * Whether the bridge's square is forbidden, as decided by the caller's
   * closure/square check (see `squares.ts`). A forbidden square rejects the
   * bridge regardless of any witness.
   */
  readonly forbiddenSquare?: boolean;
  readonly now?: number;
  readonly consumption?: WitnessConsumption;
  /** Receipt fields threaded through to the emitted {@link BridgeReceipt}. */
  readonly receipt?: Omit<AdmissionRequest, "bridgeId">;
}

/**
 * Admit a bridge into a seam, producing the four-outcome
 * {@link BridgeAdmissionVerdict}. The gates are checked in order: an invalid
 * lifecycle, then a forbidden square, then the witness (delegated to
 * {@link admitBridgeWitness}). A witness that is missing yields
 * `rejected-missing-witness`; a witness that is stale, revoked, consumed,
 * out-of-scope, theorem-mismatched, or carries an ill-formed trace yields
 * `rejected-invalid-lifecycle`. The bridge's witness requirement is derived
 * from `bridge.seam.theorem`, so it cannot drift from the seam.
 */
export function admitBridge(
  bridge: CskBridge,
  context: BridgeAdmissionContext,
): {
  readonly verdict: BridgeAdmissionVerdict;
  readonly consumption: WitnessConsumption | null;
} {
  const consumptionLedger = context.consumption ?? bridge.consumption ?? null;
  if (bridge.lifecycle !== "proposed") {
    return {
      verdict: {
        status: "rejected-invalid-lifecycle",
        bridge: bridge.id,
        reason: `bridge lifecycle is ${bridge.lifecycle}`,
      },
      consumption: consumptionLedger,
    };
  }
  if (bridge.receipt !== null) {
    return {
      verdict: {
        status: "rejected-invalid-lifecycle",
        bridge: bridge.id,
        reason: "bridge already emitted a receipt",
      },
      consumption: consumptionLedger,
    };
  }
  if (context.forbiddenSquare === true) {
    return {
      verdict: {
        status: "rejected-forbidden-square",
        bridge: bridge.id,
        reason: bridge.seam.forbiddenPattern,
      },
      consumption: consumptionLedger,
    };
  }
  const result = admitBridgeWitness(
    { scope: context.scope, theorem: bridge.seam.theorem.code },
    bridge.witness,
    { bridgeId: bridge.id, ...(context.receipt ?? {}) },
    {
      ...(context.now !== undefined ? { now: context.now } : {}),
      ...(consumptionLedger !== null ? { consumption: consumptionLedger } : {}),
    },
  );
  switch (result.verdict.status) {
    case "admitted":
      return {
        verdict: { status: "admitted", bridge: bridge.id, receipt: result.verdict.receipt },
        consumption: result.consumption,
      };
    case "missing":
      return {
        verdict: {
          status: "rejected-missing-witness",
          bridge: bridge.id,
          required: result.verdict.required,
        },
        consumption: result.consumption,
      };
    default:
      return {
        verdict: {
          status: "rejected-invalid-lifecycle",
          bridge: bridge.id,
          reason: `witness ${result.verdict.status}`,
        },
        consumption: result.consumption,
      };
  }
}

// ── Bridge matrices ────────────────────────────────────────────

/** Whether a composition of two bridge endpoints is allowed or forbidden. */
export type BridgeRuleStatus = "allowed" | "forbidden";

/**
 * One cell of a bridge-closure matrix: the verdict for composing `lhs` with
 * `rhs`, plus any witness kinds the composition requires.
 */
export interface BridgeRule {
  readonly lhs: string;
  readonly rhs: string;
  readonly status: BridgeRuleStatus;
  readonly requiredWitnessKinds: readonly WitnessKind[];
  readonly reason: string | null;
}

/** A bridge-closure matrix with a default verdict for unlisted pairs. */
export interface BridgeMatrix {
  readonly rules: readonly BridgeRule[];
  readonly unknownPair: BridgeRuleStatus;
}

interface BridgeMatrixIndex {
  readonly ruleByPair: ReadonlyMap<string, BridgeRule>;
}

const bridgeMatrixIndexes = new WeakMap<BridgeMatrix, BridgeMatrixIndex>();

function bridgeRuleKey(lhs: string, rhs: string): string {
  return `${String(lhs.length)}:${lhs}${String(rhs.length)}:${rhs}`;
}

function bridgeMatrixIndex(matrix: BridgeMatrix): BridgeMatrixIndex {
  const cached = bridgeMatrixIndexes.get(matrix);
  if (cached !== undefined) return cached;
  const ruleByPair = new Map<string, BridgeRule>();
  for (const rule of matrix.rules) {
    const key = bridgeRuleKey(rule.lhs, rule.rhs);
    if (!ruleByPair.has(key)) ruleByPair.set(key, rule);
  }
  const index = { ruleByPair };
  bridgeMatrixIndexes.set(matrix, index);
  return index;
}

/**
 * Look up the rule governing `lhs` ∘ `rhs`. When no rule lists the pair, the
 * matrix's `unknownPair` default applies.
 */
export function lookupBridgeRule(matrix: BridgeMatrix, lhs: string, rhs: string): BridgeRule {
  const found = bridgeMatrixIndex(matrix).ruleByPair.get(bridgeRuleKey(lhs, rhs));
  if (found !== undefined) return found;
  return { lhs, rhs, status: matrix.unknownPair, requiredWitnessKinds: [], reason: null };
}

// ── Zod schemas for DCS catalogs ───────────────────────────────

const witnessKindSchema = z.enum(WITNESS_KINDS);
const orientationSchema = z.enum(SEAM_ORIENTATIONS);
const theoremCodeSchema = z.enum(NEUTRALIZATION_THEOREM_CODES);
const ruleStatusSchema = z.enum(["allowed", "forbidden"]);

/** Schema for a seam row in a DCS JSON catalog. */
export const cskSeamRowSchema = z
  .object({
    id: z.string().min(1),
    orientation: orientationSchema,
    theorem: theoremCodeSchema,
    forbiddenPattern: z.string().min(1),
    requiredWitnessKinds: z.array(witnessKindSchema).optional(),
  })
  .loose()
  .superRefine((row, context) => {
    const required = witnessKindForTheorem(row.theorem);
    if (row.requiredWitnessKinds !== undefined && !row.requiredWitnessKinds.includes(required)) {
      context.addIssue({
        code: "custom",
        path: ["requiredWitnessKinds"],
        message: `requiredWitnessKinds must include theorem-governed kind ${required}`,
      });
    }
  });

/** A validated seam catalog row. */
export type CskSeamRow = z.infer<typeof cskSeamRowSchema>;

/** Schema for a bridge-matrix rule row. */
export const bridgeRuleRowSchema = z
  .object({
    lhs: z.string().min(1),
    rhs: z.string().min(1),
    status: ruleStatusSchema,
    requiredWitnessKinds: z.array(witnessKindSchema).optional(),
    reason: z.string().nullable().optional(),
  })
  .loose();

/** Schema for a full bridge matrix loaded from JSON. */
export const bridgeMatrixSchema = z
  .object({
    rules: z.array(bridgeRuleRowSchema),
    unknownPair: ruleStatusSchema.optional(),
  })
  .loose();

/** Parse a seam catalog row, returning `null` on failure. */
export function parseCskSeamRow(value: unknown): CskSeamRow | null {
  const result = cskSeamRowSchema.safeParse(value);
  return result.success ? result.data : null;
}

/** Build a {@link CskSeam} from a validated DCS catalog row. */
export function cskSeamFromRow(row: CskSeamRow): CskSeam {
  return defineSeam({
    id: row.id,
    orientation: row.orientation,
    theorem: row.theorem,
    forbiddenPattern: row.forbiddenPattern,
    ...(row.requiredWitnessKinds !== undefined
      ? { requiredWitnessKinds: row.requiredWitnessKinds }
      : {}),
  });
}

/** Parse and normalize a bridge matrix from JSON, returning `null` on failure. */
export function parseBridgeMatrix(value: unknown): BridgeMatrix | null {
  const result = bridgeMatrixSchema.safeParse(value);
  if (!result.success) return null;
  return {
    rules: result.data.rules.map((rule) => ({
      lhs: rule.lhs,
      rhs: rule.rhs,
      status: rule.status,
      requiredWitnessKinds: rule.requiredWitnessKinds ?? [],
      reason: rule.reason ?? null,
    })),
    unknownPair: result.data.unknownPair ?? "forbidden",
  };
}
